import { apiFetch } from './api';
import { uploadViaSignedUrl, type UploadProgressHandler } from './fileUpload';
import { useAuthStore } from '../store/useAuthStore';

// In-app bug reporting. Filing (POST + the draft upload URL) is open to every
// signed-in user — that is the floating widget. Everything else is the admin
// triage view in Settings and answers 403 for anyone else.

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface Envelope<T> {
  success: true;
  data: T;
}

export type BugReportStatus = 'NEW' | 'IN_PROGRESS' | 'RESOLVED' | 'REJECTED';
export type BugReportPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export const BUG_STATUSES: BugReportStatus[] = ['NEW', 'IN_PROGRESS', 'RESOLVED', 'REJECTED'];
export const BUG_PRIORITIES: BugReportPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

export interface BugAttachment {
  url: string;
  name: string;
  contentType: string;
}

interface Person {
  id: string;
  name: string;
}

export interface BugReport {
  id: string;
  number: number;
  /** "BUG-12" — what the UI shows and what people quote to each other. */
  identifier: string;
  title: string;
  description: string;
  status: BugReportStatus;
  priority: BugReportPriority;
  attachments: BugAttachment[];
  reporterId: string | null;
  reporter: Person | null;
  pageUrl: string | null;
  userAgent: string | null;
  viewportWidth: number | null;
  viewportHeight: number | null;
  appVersion: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BugComment {
  id: string;
  reportId: string;
  authorId: string | null;
  author: Person | null;
  content: string;
  attachments: BugAttachment[];
  createdAt: string;
  updatedAt: string;
}

export interface BugActivity {
  id: string;
  reportId: string;
  userId: string | null;
  user: Person | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
}

export type BugTimelineItem =
  | { kind: 'comment'; at: string; data: BugComment }
  | { kind: 'activity'; at: string; data: BugActivity };

export interface BugListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface BugListResponse {
  success: true;
  data: BugReport[];
  meta: BugListMeta;
}

export interface BugFilters {
  q?: string;
  status?: BugReportStatus;
  priority?: BugReportPriority;
  reporterId?: string;
  sort?: 'createdAt' | 'number' | 'status' | 'priority';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/** What the widget captures on its own, so the reporter never has to. */
export interface BugContext {
  pageUrl?: string | null;
  userAgent?: string | null;
  viewportWidth?: number | null;
  viewportHeight?: number | null;
  appVersion?: string | null;
}

export interface CreateBugReportInput {
  title: string;
  description: string;
  attachments?: BugAttachment[];
  context?: BugContext;
}

// --- Reporting (any signed-in user) ----------------------------------------

export async function createBugReport(input: CreateBugReportInput): Promise<BugReport> {
  const res = await apiFetch<Envelope<BugReport>>('/api/bug-reports', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

interface UploadUrlResult {
  uploadUrl: string;
  key: string;
  publicUrl: string;
  expiresIn: number;
}

async function requestDraftUploadUrl(
  filename: string,
  contentType: string,
): Promise<UploadUrlResult> {
  const res = await apiFetch<Envelope<UploadUrlResult>>('/api/bug-reports/upload-url', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ filename, contentType }),
  });
  return res.data;
}

/**
 * Upload a screenshot for a report that does not exist yet — the widget lets
 * you drop the image in while you are still typing, so the upload cannot be
 * keyed on a report id. The backend namespaces the key per reporter instead.
 */
export function uploadBugDraftFile(
  file: File,
  onProgress?: UploadProgressHandler,
): Promise<BugAttachment> {
  return uploadViaSignedUrl(file, requestDraftUploadUrl, onProgress);
}

// --- Triage (ADMIN) --------------------------------------------------------

export async function listBugReports(filters: BugFilters = {}): Promise<BugListResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return apiFetch<BugListResponse>(`/api/bug-reports${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
}

export interface BugStats {
  newCount: number;
  total: number;
}

export async function getBugStats(): Promise<BugStats> {
  const res = await apiFetch<Envelope<BugStats>>('/api/bug-reports/stats', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getBugReport(id: string): Promise<BugReport> {
  const res = await apiFetch<Envelope<BugReport>>(`/api/bug-reports/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export interface UpdateBugReportInput {
  status?: BugReportStatus;
  priority?: BugReportPriority;
}

export async function updateBugReport(id: string, input: UpdateBugReportInput): Promise<BugReport> {
  const res = await apiFetch<Envelope<BugReport>>(`/api/bug-reports/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteBugReport(id: string): Promise<void> {
  await apiFetch<void>(`/api/bug-reports/${id}`, { method: 'DELETE', headers: authHeaders() });
}

export async function getBugTimeline(id: string): Promise<BugTimelineItem[]> {
  const res = await apiFetch<Envelope<{ timeline: BugTimelineItem[] }>>(
    `/api/bug-reports/${id}/timeline`,
    { headers: authHeaders() },
  );
  return res.data.timeline;
}

export async function addBugComment(
  reportId: string,
  content: string,
  attachments: BugAttachment[] = [],
): Promise<BugComment> {
  const res = await apiFetch<Envelope<BugComment>>(`/api/bug-reports/${reportId}/comments`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ content, ...(attachments.length ? { attachments } : {}) }),
  });
  return res.data;
}

export async function deleteBugComment(reportId: string, commentId: string): Promise<void> {
  await apiFetch<void>(`/api/bug-reports/${reportId}/comments/${commentId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

/** Attachment on a triage comment — keyed on the report, like task comments. */
export function uploadBugCommentFile(
  reportId: string,
  file: File,
  onProgress?: UploadProgressHandler,
): Promise<BugAttachment> {
  return uploadViaSignedUrl(
    file,
    async (filename, contentType) => {
      const res = await apiFetch<Envelope<UploadUrlResult>>(
        `/api/bug-reports/${reportId}/upload-url`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ filename, contentType }),
        },
      );
      return res.data;
    },
    onProgress,
  );
}
